import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Compass,
    Hammer,
    Home as HomeIcon,
    ChefHat,
    Layers,
    Sparkles,
    ArrowRight,
    Quote,
} from "lucide-react";
import {
    COMPANY,
    SERVICES,
    PROCESS,
    PORTFOLIO,
    BEFORE_AFTER,
    TESTIMONIALS,
    STATS,
} from "../lib/siteData";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import EnquiryForm from "../components/EnquiryForm";
import { LogoTile } from "../components/Logo";

const SERVICE_ICONS = {
    "interior-design": Compass,
    "renovation": Hammer,
    "construction": HomeIcon,
    "kitchen-cabinetry": ChefHat,
    "ceilings-flooring": Layers,
    "refurbishing-decor": Sparkles,
};

export default function HomePage() {
    return (
        <div data-testid="page-home">
            <Hero />
            <MarqueeStrip />
            <IntroSection />
            <ServicesGrid />
            <BeforeAfterFeature />
            <PortfolioTeaser />
            <ProcessSection />
            <Testimonials />
            <StatsBanner />
            <ContactCTA />
        </div>
    );
}

/* ---------------- HERO ---------------- */
const Hero = () => (
    <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7 fade-up">
                <div className="eyebrow mb-6">
                    Interior · Construction · Decoration
                </div>
                <h1 className="font-display text-[44px] leading-[1.04] sm:text-6xl md:text-[80px] md:leading-[0.98] tracking-[-0.03em] text-hhc-ink">
                    Design your home.
                    <br />
                    <em className="not-italic text-hhc-clay font-normal font-display">
                        Plan it, do it.
                    </em>
                </h1>
                <p className="mt-8 text-hhc-muted text-[17px] leading-relaxed max-w-xl">
                    A Kuala Lumpur & Selangor studio for bespoke interiors,
                    renovations, and cabinetry. We take the brief off your
                    shoulders and return a home that quietly reflects how you
                    actually live.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                    <Link
                        to="/contact"
                        className="btn-primary text-[13px]"
                        data-testid="hero-cta-primary"
                    >
                        Start your project
                        <ArrowUpRight size={16} strokeWidth={1.75} />
                    </Link>
                    <Link
                        to="/portfolio"
                        className="btn-outline text-[13px]"
                        data-testid="hero-cta-portfolio"
                    >
                        View portfolio
                    </Link>
                </div>
            </div>

            <div className="md:col-span-5 relative">
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_40px_80px_-40px_rgba(31,35,33,0.35)]">
                    <img
                        src="https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
                        alt="Warm modern Malaysian living room by HEYHOMECAN"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute -left-6 -bottom-6 md:-left-16 md:-bottom-14 w-40 md:w-56 aspect-[3/4] rounded-2xl overflow-hidden hidden sm:block shadow-[0_30px_60px_-30px_rgba(31,35,33,0.45)] rotate-[-3deg]">
                    <img
                        src="https://images.unsplash.com/photo-1639405069836-f82aa6dcb900?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwY2FiaW5ldHJ5fGVufDB8fHx8MTc4MzQzNjg0OHww&ixlib=rb-4.1.0&q=85"
                        alt="Custom kitchen cabinetry"
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="hidden md:flex absolute top-4 right-4 bg-hhc-ivory/90 backdrop-blur rounded-2xl border border-hhc-line p-4 gap-3 items-center max-w-[220px]">
                    <LogoTile size={44} />
                    <div>
                        <div className="text-[10px] tracking-[0.28em] uppercase text-hhc-clay">
                            Studio
                        </div>
                        <div className="text-[13px] text-hhc-ink font-medium mt-0.5">
                            {COMPANY.serviceArea}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ---------------- MARQUEE ---------------- */
const MarqueeStrip = () => {
    const words = [
        "Interior Design",
        "Construction",
        "Renovation",
        "Kitchen Cabinetry",
        "Ceilings & Flooring",
        "Refurbishing",
        "Decoration",
        "Full-Home Cabinetry",
    ];
    const doubled = [...words, ...words];
    return (
        <section className="border-y border-hhc-line/80 bg-hhc-cream/60 py-8 overflow-hidden">
            <div className="marquee-track flex gap-14 whitespace-nowrap min-w-[200%]">
                {doubled.map((w, i) => (
                    <div
                        key={i}
                        className="flex items-center gap-14 font-display text-[26px] md:text-[36px] text-hhc-ink/85"
                    >
                        <span>{w}</span>
                        <span className="w-2 h-2 rounded-full bg-hhc-clay" />
                    </div>
                ))}
            </div>
        </section>
    );
};

/* ---------------- INTRO ---------------- */
const IntroSection = () => (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
            <div className="md:col-span-5">
                <div className="eyebrow mb-6">Our approach</div>
                <h2 className="font-display text-4xl md:text-5xl leading-[1.05] text-hhc-ink">
                    A quieter kind of renovation studio.
                </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7">
                <p className="text-[17px] text-hhc-muted leading-[1.75]">
                    We specialise in transforming spaces with custom-tailored
                    interior design, construction, and renovation solutions.
                    From kitchens and full-home cabinetry to ceilings, flooring,
                    and refurbishing — we handle every detail to bring your
                    vision to life.
                </p>
                <p className="text-[17px] text-hhc-muted leading-[1.75] mt-6">
                    Our focus is on delivering high-quality, bespoke designs
                    that combine style, functionality, and craftsmanship. Whether
                    it's a small update or a full renovation, we're here to
                    create spaces that truly feel like home.
                </p>
                <Link
                    to="/about"
                    className="mt-8 inline-flex items-center gap-2 text-hhc-ink text-[13px] tracking-[0.18em] uppercase font-medium border-b border-hhc-ink pb-1 hover:text-hhc-clay hover:border-hhc-clay transition-colors"
                    data-testid="intro-learn-more"
                >
                    About our studio
                    <ArrowRight size={14} />
                </Link>
            </div>
        </div>
    </section>
);

/* ---------------- SERVICES ---------------- */
const ServicesGrid = () => (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="section-services">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-14">
            <div>
                <div className="eyebrow mb-4">What we do</div>
                <h2 className="font-display text-4xl md:text-5xl text-hhc-ink max-w-2xl">
                    Every detail, handled in-house.
                </h2>
            </div>
            <Link
                to="/services"
                className="text-[13px] tracking-[0.2em] uppercase font-medium text-hhc-ink border-b border-hhc-ink pb-1 hover:text-hhc-clay hover:border-hhc-clay transition-colors"
            >
                All services →
            </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 reveal-stagger">
            {SERVICES.map((s) => {
                const Icon = SERVICE_ICONS[s.slug] || Sparkles;
                return (
                    <div
                        key={s.slug}
                        className="group relative rounded-2xl bg-white border border-hhc-line overflow-hidden hover-lift"
                        data-testid={`service-card-${s.slug}`}
                    >
                        <div className="aspect-[4/3] overflow-hidden">
                            <img
                                src={s.image}
                                alt={s.title}
                                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                            />
                        </div>
                        <div className="p-7">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full grid place-items-center bg-hhc-cream text-hhc-clay">
                                    <Icon size={18} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-display text-xl text-hhc-ink">
                                    {s.title}
                                </h3>
                            </div>
                            <p className="mt-4 text-[14px] text-hhc-muted leading-relaxed">
                                {s.summary}
                            </p>
                            <Link
                                to={`/services#${s.slug}`}
                                className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-hhc-ink group-hover:text-hhc-clay transition-colors"
                            >
                                Learn more <ArrowRight size={13} />
                            </Link>
                        </div>
                    </div>
                );
            })}
        </div>
    </section>
);

/* ---------------- BEFORE / AFTER ---------------- */
const BeforeAfterFeature = () => {
    const item = BEFORE_AFTER[0];
    return (
        <section className="bg-hhc-cream/50 border-y border-hhc-line/70 py-24 md:py-32" data-testid="section-before-after">
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
                <div className="md:col-span-4">
                    <div className="eyebrow mb-4">Before · After</div>
                    <h2 className="font-display text-4xl md:text-5xl text-hhc-ink leading-[1.05]">
                        Watch a space become a home.
                    </h2>
                    <p className="mt-6 text-hhc-muted leading-relaxed max-w-md">
                        Slide across to see how our team transforms tired
                        interiors into calm, contemporary Malaysian homes.
                    </p>
                    <Link
                        to="/portfolio"
                        className="btn-outline text-[13px] mt-8"
                        data-testid="before-after-view-more"
                    >
                        See more transformations
                        <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="md:col-span-8">
                    <BeforeAfterSlider
                        before={item.before}
                        after={item.after}
                        title={item.title}
                    />
                    <p className="mt-4 text-[12px] tracking-[0.22em] uppercase text-hhc-muted">
                        {item.title}
                    </p>
                </div>
            </div>
        </section>
    );
};

/* ---------------- PORTFOLIO TEASER ---------------- */
const PortfolioTeaser = () => (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="section-portfolio-teaser">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-14">
            <div>
                <div className="eyebrow mb-4">Selected work</div>
                <h2 className="font-display text-4xl md:text-5xl text-hhc-ink max-w-2xl">
                    Recent projects across KL & Selangor.
                </h2>
            </div>
            <Link
                to="/portfolio"
                className="text-[13px] tracking-[0.2em] uppercase font-medium text-hhc-ink border-b border-hhc-ink pb-1 hover:text-hhc-clay hover:border-hhc-clay transition-colors"
            >
                Full portfolio →
            </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {PORTFOLIO.slice(0, 5).map((p) => (
                <Link
                    key={p.id}
                    to={`/portfolio#${p.id}`}
                    className={`group relative overflow-hidden rounded-2xl ${p.span} block hover-lift`}
                    data-testid={`portfolio-card-${p.id}`}
                >
                    <div className="aspect-[4/3] md:aspect-auto md:h-full min-h-[300px]">
                        <img
                            src={p.cover}
                            alt={p.title}
                            className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                        />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-hhc-ink/85 via-hhc-ink/40 to-transparent text-hhc-ivory">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-hhc-sun mb-2">
                            {p.category}
                        </div>
                        <div className="font-display text-xl md:text-2xl">
                            {p.title}
                        </div>
                        <div className="text-[12px] text-hhc-cream/70 mt-1">
                            {p.location} · {p.year}
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    </section>
);

/* ---------------- PROCESS ---------------- */
const ProcessSection = () => (
    <section className="bg-hhc-ink text-hhc-cream py-24 md:py-32" data-testid="section-process">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
                <div className="md:col-span-5">
                    <div className="text-[11px] tracking-[0.28em] uppercase text-hhc-sun mb-4">
                        The Process
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl leading-[1.05] text-hhc-ivory">
                        Four quiet steps between a brief and a finished home.
                    </h2>
                </div>
                <div className="md:col-span-6 md:col-start-7 flex items-end">
                    <p className="text-hhc-cream/70 leading-relaxed">
                        We keep the process transparent — no theatrics, no
                        surprises. Every phase has a defined outcome, a fixed
                        timeline, and a single point of accountability.
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
                {PROCESS.map((p, i) => (
                    <div
                        key={p.step}
                        className="relative pt-8 border-t border-hhc-cream/20"
                        data-testid={`process-step-${p.step}`}
                    >
                        <div className="text-hhc-sun font-display text-6xl leading-none">
                            {p.step}
                        </div>
                        <h3 className="mt-6 font-display text-2xl text-hhc-ivory">
                            {p.title}
                        </h3>
                        <p className="mt-4 text-hhc-cream/75 text-[14px] leading-relaxed">
                            {p.text}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------------- TESTIMONIALS ---------------- */
const Testimonials = () => (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="section-testimonials">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
            <div className="md:col-span-6">
                <div className="eyebrow mb-4">Word of mouth</div>
                <h2 className="font-display text-4xl md:text-5xl text-hhc-ink leading-[1.05]">
                    What our clients say when the dust settles.
                </h2>
            </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
                <div
                    key={i}
                    className="rounded-2xl bg-hhc-cream/60 border border-hhc-line p-8 hover-lift"
                    data-testid={`testimonial-${i}`}
                >
                    <Quote size={22} className="text-hhc-clay" />
                    <p className="mt-4 font-display text-[22px] leading-[1.35] text-hhc-ink">
                        “{t.quote}”
                    </p>
                    <div className="mt-8 pt-6 border-t border-hhc-line/60">
                        <div className="text-[14px] text-hhc-ink font-medium">
                            {t.name}
                        </div>
                        <div className="text-[12px] text-hhc-muted mt-1 tracking-wide">
                            {t.location}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </section>
);

/* ---------------- STATS ---------------- */
const StatsBanner = () => (
    <section className="bg-hhc-cream/60 border-y border-hhc-line/70 py-16" data-testid="section-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
                <div key={i} className="text-center md:text-left" data-testid={`stat-${i}`}>
                    <div className="font-display text-5xl md:text-6xl text-hhc-ink">
                        {s.value}
                    </div>
                    <div className="mt-2 text-[11px] tracking-[0.28em] uppercase text-hhc-muted">
                        {s.label}
                    </div>
                </div>
            ))}
        </div>
    </section>
);

/* ---------------- CONTACT CTA ---------------- */
const ContactCTA = () => (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" id="enquiry" data-testid="section-contact-cta">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
            <div className="md:col-span-5">
                <div className="eyebrow mb-4">Start a project</div>
                <h2 className="font-display text-4xl md:text-5xl text-hhc-ink leading-[1.05]">
                    Tell us about your home.
                </h2>
                <p className="mt-6 text-hhc-muted leading-relaxed max-w-md">
                    Share a few details and our studio will reply within 48
                    hours with next steps, references, and an initial call
                    schedule.
                </p>
                <div className="mt-10 space-y-3 text-[14px] text-hhc-graphite">
                    <p><span className="text-hhc-muted">Studio:</span> {COMPANY.address}</p>
                    <p><span className="text-hhc-muted">Email:</span> <a className="hover:text-hhc-clay transition-colors" href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></p>
                    <p><span className="text-hhc-muted">Phone:</span> <a className="hover:text-hhc-clay transition-colors" href={`tel:+${COMPANY.phoneRaw}`}>{COMPANY.phone}</a></p>
                </div>
            </div>
            <div className="md:col-span-7 rounded-2xl bg-white border border-hhc-line p-8 md:p-12">
                <EnquiryForm />
            </div>
        </div>
    </section>
);
